import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/auth-context';
import { MarcaLogin } from '../layout/Logo';

/**
 * Só caminho interno é aceito como destino: o `state` vem da navegação, e
 * aceitar um valor externo aqui viraria redirecionamento aberto. As rotas
 * públicas ficam de fora porque voltar para elas já logado recairia no mesmo
 * `<Navigate>`, em laço.
 */
function destinoValido(de: string | undefined): de is string {
  if (!de?.startsWith('/') || de.startsWith('//')) return false;
  return de !== '/entrar' && de !== '/convite';
}

/**
 * O que a tela lembra entre uma entrada e a seguinte.
 *
 * ⚠️ **Só o e-mail e a preferência do "lembrar de mim". Senha, nunca.** Guardar
 * senha é trabalho do gerenciador do navegador, que a cifra com a conta do
 * sistema operacional; uma cópia nossa em `localStorage` seria texto puro
 * legível por qualquer script da origem, e trocaria uma conveniência por uma
 * credencial exposta.
 *
 * O e-mail é outra coisa: identifica, não autentica. É o mesmo que o Gmail faz,
 * e é o que a pessoa pediu — digitar o mesmo endereço toda vez é trabalho que a
 * tela estava passando para quem a usa.
 */
const ULTIMO_EMAIL = 'mpa:ultimo-email';
const ULTIMO_LEMBRAR = 'mpa:lembrar-de-mim';

/**
 * Leitura e escrita que não derrubam a tela.
 *
 * `localStorage` **lança** em janela anônima com cookies de terceiros
 * bloqueados, em navegador com dados de site desligados, e dentro de alguns
 * webviews. Sem o `try`, a exceção sobe no primeiro render e a tela de login
 * — a única porta do produto — fica em branco por causa de uma conveniência.
 */
function lembrado(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}

function lembrar(chave: string, valor: string) {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    // Sem memória entre sessões neste navegador. A tela funciona igual.
  }
}

/**
 * O "Salvar senha?" do navegador, pedido com todas as letras.
 *
 * Os campos já dizem `username` e `current-password`, e isso basta para o
 * navegador PREENCHER — mas para ele OFERECER salvar, precisa perceber que o
 * login deu certo. Num site que entra sem recarregar a página (é o caso: o
 * envio é um `fetch` e a tela troca sozinha) essa percepção é palpite, e o
 * Chrome às vezes não pergunta. Sem senha salva, a próxima visita não tem o que
 * sugerir — era o que se via em 16/10/2026: outros sites mostravam as contas
 * guardadas no campo de e-mail, este não.
 *
 * `PasswordCredential` lê o formulário pelos mesmos `autocomplete`. Existe no
 * Chrome e no Edge; nos outros a função não faz nada e vale a heurística de
 * cada um. A senha não passa por nós: vai do formulário direto para o cofre do
 * navegador.
 */
type ConstrutorDeCredencial = new (form: HTMLFormElement) => Credential;

function credencialDoFormulario(form: HTMLFormElement): Credential | null {
  const Construtor = (window as unknown as { PasswordCredential?: ConstrutorDeCredencial })
    .PasswordCredential;
  if (!Construtor) return null;

  try {
    return new Construtor(form);
  } catch {
    return null;
  }
}

/**
 * Estrutura e medidas seguem `teladeloginvisual` (raiz do repo): marca, título,
 * formulário, divisória, rodapé — nessa ordem, dentro de um cartão único.
 */
export function Login() {
  const { entrar, usuario } = useAuth();
  const location = useLocation();
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [ajudaSenha, setAjudaSenha] = useState(false);

  /**
   * Lido UMA vez, na montagem, e nunca depois.
   *
   * O inicializador de `useState` roda só no primeiro render, e é isso que
   * mantém os campos **não controlados**: o React dá o valor inicial e sai da
   * frente. Importa porque o preenchimento automático do navegador escreve
   * direto no DOM — um `value` controlado brigaria com ele e desfaria o próprio
   * comportamento que esta tela quer ter.
   */
  const [emailInicial] = useState(() => lembrado(ULTIMO_EMAIL) ?? '');
  const [lembrarInicial] = useState(() => lembrado(ULTIMO_LEMBRAR) === '1');

  async function submeter(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);

    const dados = new FormData(e.currentTarget);
    const email = String(dados.get('email'));
    const querLembrar = dados.get('lembrar') === 'on';
    // Lida agora: depois do `await` a tela já pode ter trocado e o formulário
    // não existe mais.
    const credencial = credencialDoFormulario(e.currentTarget);

    try {
      await entrar(email, String(dados.get('senha')), querLembrar);

      // Só com o login confirmado — senha errada não deve ir para o cofre. Sem
      // `await`: é um aviso do navegador, e a entrada não espera por ele.
      if (credencial) navigator.credentials.store(credencial).catch(() => undefined);

      /**
       * Só depois de entrar, e nunca antes.
       *
       * Gravar no envio guardaria o e-mail errado de quem digitou errado — e aí
       * a tela passaria a sugerir o erro de volta, toda vez, com cara de
       * memória.
       */
      lembrar(ULTIMO_EMAIL, email);
      lembrar(ULTIMO_LEMBRAR, querLembrar ? '1' : '0');

      // A navegação acontece no `<Navigate>` abaixo, disparado pelo usuário
      // entrar no contexto — não aqui.
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível entrar.');
    } finally {
      setEnviando(false);
    }
  }

  /**
   * Saída da tela de login, para dois casos: acabou de entrar, ou chegou aqui
   * com sessão viva (favorito, botão de voltar).
   *
   * ⚠️ Tem de ser explícito. O `RequireAuth` NÃO resolve isto: ele manda para cá
   * com `<Navigate>` e, ao fazer isso, se desmonta — quando o usuário aparece no
   * contexto não há mais nada montado para reagir. O login funcionava e a pessoa
   * ficava parada no formulário.
   */
  if (usuario) {
    const de = (location.state as { de?: string } | null)?.de;
    // O destino sai do escopo, não da URL de entrada: existe uma tela de login
    // só, e é o `scope` que decide se a pessoa opera a plataforma ou um cliente.
    const padrao = usuario.scope === 'SUPERADMIN' ? '/master' : '/dashboard';
    return <Navigate to={destinoValido(de) ? de : padrao} replace />;
  }

  return (
    <div className="login-tela">
      <div className="login-caixa">
        <MarcaLogin />

        <div className="login-titulo">Entrar na plataforma</div>

        <form onSubmit={submeter}>
          {erro && (
            <div className="form-erro">
              <i className="ti ti-alert-circle" style={{ fontSize: 13 }} /> {erro}
            </div>
          )}

          {ajudaSenha && (
            <div className="login-aviso">
              <i className="ti ti-info-circle" style={{ fontSize: 13, marginTop: 2 }} />
              <span>
                Não há recuperação por e-mail: quem cria e reativa contas é o administrador da sua
                organização. Peça um novo convite — ele chega com um link para definir a senha.
              </span>
            </div>
          )}

          <label className="campo">
            <span className="campo-label">E-mail</span>
            <input
              className="login-input"
              name="email"
              type="email"
              autoComplete="username"
              required
              defaultValue={emailInicial}
              // O foco vai para onde falta digitar. Com o e-mail já preenchido,
              // focar o campo dele obrigaria a um Tab antes de cada entrada —
              // que é metade do incômodo que lembrar o e-mail resolve.
              autoFocus={!emailInicial}
              placeholder="seu@email.com"
            />
          </label>

          <label className="campo">
            <span className="campo-label">Senha</span>
            <div className="login-senha">
              <input
                className="login-input"
                name="senha"
                type={mostrarSenha ? 'text' : 'password'}
                autoComplete="current-password"
                required
                autoFocus={!!emailInicial}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="login-olho"
                onClick={() => setMostrarSenha((v) => !v)}
                title="Mostrar/ocultar senha"
                aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                // Sem isto o clique no olho envia o formulário no Safari e
                // rouba o foco do campo em todos os navegadores.
                tabIndex={-1}
              >
                <i className={mostrarSenha ? 'ti ti-eye-off' : 'ti ti-eye'} />
              </button>
            </div>
          </label>

          <div className="login-linha">
            <label className="login-lembrar">
              <input type="checkbox" name="lembrar" defaultChecked={lembrarInicial} />
              Lembrar de mim
            </label>

            <button type="button" className="login-link" onClick={() => setAjudaSenha((v) => !v)}>
              Esqueci minha senha
            </button>
          </div>

          <button className="btn btn-primary login-botao" type="submit" disabled={enviando}>
            {enviando ? (
              <>
                <i className="ti ti-loader-2 girando" style={{ fontSize: 14 }} /> Entrando…
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        <hr className="login-divisor" />

        <div className="login-rodape">
          Acesso restrito a usuários cadastrados.
          <br />
          Entre em contato com o administrador da sua organização.
        </div>
      </div>
    </div>
  );
}
